import { useState } from 'react';
import { ErrorApi } from '../api/problema';
import { useAvisoBreve } from '../componentes/avisoBreve';
import { Boton } from '../componentes/Boton';
import { Campo } from '../componentes/Campo';
import { Desplegable } from '../componentes/Desplegable';
import { Icono } from '../componentes/Icono';
import { useCabecera } from '../marco/cabecera';
import { SUBTITULOS, ayudaDe, cambiosDatos, erroresDatos, etiquetasFiscales, formDatosDe, ibanOculto, paisesFiscales, textoCondiciones, textoTipo, type FormDatos } from './logica';
import type { DatosPortal } from './carga';

/**
 * Datos fiscales: forma jurídica y razón social en solo lectura con «escríbenos» (los cambia Reservaria, G237); teléfono,
 * dirección fiscal, email para facturas e IBAN editables (`updatePartnerProfile`, solo lo que cambia); tipo de partner y
 * condiciones aceptadas (G1089). «Guardar» va en la banda. El NIF sale enmascarado (`tax_id_masked`, G1096) y la dirección
 * fiscal va en cuatro campos, Dirección, CP, Ciudad y País (G1095), que se mandan juntos. NIF y País están hechos con las
 * piezas y estilos que ya había: **se ajustarán al llegar C.57** (dibujo de diseño).
 */
export function DatosFiscales({ d }: { d: DatosPortal }) {
    const { perfil } = d;
    const avisar = useAvisoBreve();
    const [f, setF] = useState<FormDatos>(() => formDatosDe(perfil));
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [guardado, setGuardado] = useState(false);
    const errores = intentado ? erroresDatos(f) : {};
    const cambios = cambiosDatos(perfil, f);
    const etiquetas = etiquetasFiscales(perfil.legal_form);

    const poner = (cambio: Partial<FormDatos>) => {
        setF((x) => ({ ...x, ...cambio }));
        setGuardado(false);
    };
    const guardar = () => {
        if (Object.keys(erroresDatos(f)).length > 0) {
            setIntentado(true);
            return;
        }
        if (cambios === null || ocupado) {
            return;
        }
        setOcupado(true);
        d.ops.updatePartnerProfile(cambios)
            .then((p) => {
                d.alGuardarPerfil(p);
                setF(formDatosDe(p));
                setIntentado(false);
                setGuardado(true);
                avisar({ tipo: 'ok', texto: 'Guardado. Se usan desde la próxima liquidación.' });
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.estado === 401) {
                    d.onSesionCaducada();
                    return;
                }
                avisar({ tipo: 'error', texto: e instanceof ErrorApi ? (e.problema.detail ?? e.problema.title) : 'No se han podido guardar los cambios. Prueba de nuevo.' });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    useCabecera({
        ayuda: ayudaDe('datos', d.pct),
        acciones: [{ clave: guardado ? 'guardado' : 'guardar', texto: guardado ? 'Guardado' : 'Guardar', icono: guardado ? 'check-check' : 'check', desactivado: ocupado, onClick: guardar }],
        subtitulo: SUBTITULOS.datos,
    });

    const condiciones = textoCondiciones(perfil);
    const nota = cambios !== null ? 'Cambios sin guardar.' : guardado ? 'Guardado. Se usan desde la próxima liquidación.' : 'Los cambios se aplican desde la próxima liquidación.';

    return (
        <div className="rv-pt-seccion">
            <div className="rv-pt-tarjeta rv-pt-datos">
                <div className="rv-pt-datos__fijos">
                    {perfil.legal_form != null && (
                        <div>
                            <span>Forma jurídica</span>
                            <strong>{etiquetas.forma}</strong>
                        </div>
                    )}
                    <div>
                        <span>{etiquetas.nombre}</span>
                        <strong>{perfil.legal_name}</strong>
                    </div>
                    {perfil.tax_id_masked != null && perfil.tax_id_masked !== '' && (
                        <div>
                            <span>{etiquetas.nif}</span>
                            <strong className="rv-num">{perfil.tax_id_masked}</strong>
                        </div>
                    )}
                </div>
                <p className="rv-pt-datos__nota">
                    <Icono nombre="lock" />
                    <span>La forma jurídica, el nombre fiscal y el NIF son los de tu solicitud aprobada. Para cambiarlos, escríbenos: lo revisamos antes de la próxima liquidación.</span>
                </p>
                <div className="rv-pt-datos__campos">
                    <Campo etiqueta="Email para las facturas" tipo="email" valor={f.emailFacturas} error={errores.emailFacturas} onChange={(v) => { poner({ emailFacturas: v }); }} />
                    <Campo etiqueta="Teléfono" tipo="tel" valor={f.telefono} error={errores.telefono} onChange={(v) => { poner({ telefono: v }); }} />
                </div>
                <div className="rv-pt-datos__campos rv-pt-datos__campos--direccion">
                    <Campo etiqueta="Dirección fiscal" autocompletar="address-line1" valor={f.direccion.calle} error={errores.calle} onChange={(v) => { poner({ direccion: { ...f.direccion, calle: v } }); }} />
                    <Campo etiqueta="Código postal" autocompletar="postal-code" valor={f.direccion.cp} error={errores.cp} onChange={(v) => { poner({ direccion: { ...f.direccion, cp: v } }); }} />
                    <Campo etiqueta="Ciudad" autocompletar="address-level2" valor={f.direccion.ciudad} error={errores.ciudad} onChange={(v) => { poner({ direccion: { ...f.direccion, ciudad: v } }); }} />
                    <Desplegable etiqueta="País" valor={f.direccion.pais} opciones={paisesFiscales(f.direccion.pais)} error={errores.pais} onChange={(v) => { poner({ direccion: { ...f.direccion, pais: v } }); }} />
                </div>
                <div className="rv-pt-datos__filas">
                    <div className="rv-pt-datos__fila">
                        <span>
                            <strong>IBAN para los pagos</strong>
                            <span className="rv-num">{ibanOculto(perfil.iban_last4) || 'Sin IBAN'}</span>
                        </span>
                        {f.iban === null && (
                            <Boton tipo="secundario" icono="pencil" onClick={() => { poner({ iban: '' }); }}>
                                Cambiar el IBAN
                            </Boton>
                        )}
                    </div>
                    {f.iban !== null && (
                        <div className="rv-pt-datos__iban">
                            <Campo etiqueta="IBAN nuevo" valor={f.iban} mayusculas="characters" autocompletar="off" error={errores.iban} ayuda="Vale desde la próxima liquidación." onChange={(v) => { poner({ iban: v }); }} />
                        </div>
                    )}
                    <div className="rv-pt-datos__fila">
                        <span>
                            <strong>Tipo de partner</strong>
                            <span>{`${textoTipo(perfil.type)} · lo decide Reservaria al aprobarte`}</span>
                        </span>
                    </div>
                    {condiciones !== null && (
                        <div className="rv-pt-datos__fila">
                            <span>
                                <strong>Condiciones del programa</strong>
                                <span>{condiciones}</span>
                            </span>
                        </div>
                    )}
                </div>
                <span className="rv-pt-datos__estado" aria-live="polite">{nota}</span>
            </div>
        </div>
    );
}
